import { computed, effect, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { ActivatePayload, AuthService, RegisterPayload } from './auth.service';
import { UserModel } from '@models/resources/user.model';
import { environment } from '../../../../environments/environment';

interface LoginOrActivateResponse {
  token: string;
  user: {
    id: number;
    email: string;
    role: UserModel['role'];
    is_active: boolean;
  };
}

const STORAGE_KEY = 'gold_user';

/**
 * Liest den persistierten User aus `sessionStorage`. Bei korrumpiertem JSON
 * wird der Eintrag still gelöscht und `null` zurückgegeben — schützt die
 * App-Initialisierung vor `JSON.parse`-Crashes (S4-1 Finding B-05).
 */
function readUserFromStorage(): UserModel | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserModel;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * HTTP-basierter Auth-Service gegen das Laravel-Backend V4
 * (Sprint 5b ADR-005).
 *
 * BE-Contract:
 * - `POST /api/login` → `{ token, user }` (einstufig, kein 2FA)
 * - `POST /api/auth/register` → `202` (Mail-Trigger, keine Anmeldung)
 * - `POST /api/auth/activate` → `{ token, user }`
 * - `POST /api/auth/activate/resend` → `202`
 * - `POST /api/auth/password/forgot` → `204` (Endpoint in Sprint 6)
 * - `POST /api/auth/password/reset` → `204` (Endpoint in Sprint 6)
 * - `POST /api/logout` → `200`
 * - `GET  /api/me` → `{ id, email, role, is_active }`
 *
 * **V4-Cleanup (Sprint 5c S5c-4):** 2FA-Endpoints (`/auth/verify-2fa`,
 * `/auth/resend-2fa`) sind im Backend seit Sprint 5b gelöscht — die
 * entsprechenden Service-Methoden hier ebenfalls.
 */
@Injectable()
export class HttpAuthService extends AuthService {
  readonly user: WritableSignal<UserModel | null>;
  readonly isAuthenticated = computed(() => this.user()?.token != null);

  constructor(private http: HttpClient) {
    super();
    this.user = signal<UserModel | null>(readUserFromStorage());

    effect(() => {
      const current = this.user();
      if (current) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    });
  }

  login(email: string, password: string): Observable<UserModel> {
    return this.http
      .post<LoginOrActivateResponse>(environment.apiUrl + '/login', { email, password })
      .pipe(
        map((res) => this.toUserModel(res.token, res.user)),
        tap((user) => this.user.set(user)),
      );
  }

  register(payload: RegisterPayload): Observable<void> {
    return this.http
      .post<{ message: string }>(environment.apiUrl + '/auth/register', payload)
      .pipe(map(() => void 0));
  }

  activate(payload: ActivatePayload): Observable<void> {
    // Defense-in-depth-Pivot 2026-05-13: BE gibt nach Activate **keinen**
    // Sanctum-Token mehr zurück. Customer muss sich anschließend explizit
    // einloggen — wir setzen hier also weder `user` noch persistieren
    // einen Token. Response ist `{ message: string }`.
    return this.http
      .post<{ message: string }>(environment.apiUrl + '/auth/activate', payload)
      .pipe(map(() => void 0));
  }

  resendActivation(email: string): Observable<void> {
    return this.http
      .post<{ message: string }>(environment.apiUrl + '/auth/activate/resend', { email })
      .pipe(map(() => void 0));
  }

  requestPasswordReset(email: string): Observable<void> {
    return this.http
      .post<void>(environment.apiUrl + '/auth/password/forgot', { email })
      .pipe(map(() => void 0));
  }

  resetPassword(token: string, password: string, email: string): Observable<void> {
    return this.http
      .post<void>(environment.apiUrl + '/auth/password/reset', {
        token,
        password,
        password_confirmation: password,
        email,
      })
      .pipe(map(() => void 0));
  }

  logout(): Observable<void> {
    return this.http.post<void>(environment.apiUrl + '/logout', {}).pipe(
      catchError(() => of(void 0)),
      tap(() => this.clear()),
      map(() => void 0),
    );
  }

  hydrate(): Observable<UserModel | null> {
    const cached = this.user();
    if (!cached?.token) {
      return of(null);
    }
    return this.http
      .get<LoginOrActivateResponse['user']>(environment.apiUrl + '/me')
      .pipe(
        map((me) => this.toUserModel(cached.token!, me)),
        tap((user) => this.user.set(user)),
        catchError(() => {
          this.clear();
          return of(null);
        }),
      );
  }

  clear(): void {
    this.user.set(null);
  }

  private toUserModel(token: string, me: LoginOrActivateResponse['user']): UserModel {
    return {
      id: me.id,
      email: me.email,
      role: me.role,
      is_active: me.is_active,
      token,
    };
  }
}
